import { useState, useRef, useEffect, ChangeEvent, useCallback } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import type { ContentBlock, Base64ContentBlock } from "@/features/chat/types/messages";
import { fileToContentBlock, type FileToContentBlockOptions } from "@/lib/storage";
import {
  getFileUploadError,
  FileUploadError,
  isSupportedFileType,
  isSupportedAudioType,
  validateFile,
  validateImageDimensions,
  FILE_SIZE_LIMITS,
  isSupportedImageType,
  SUPPORTED_PDF_TYPE,
} from "@/constants";
import {
  optimizeForAnthropicApi,
  isCompressibleImage,
} from "@/lib/image-compression";
import { extractPhotoDate } from "@/lib/extract-photo-date";

/**
 * Map file extensions to MIME types for fallback detection.
 * Used when browser doesn't report a MIME type.
 */
const EXTENSION_TO_MIME: Record<string, string> = {
  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  // PDF
  pdf: "application/pdf",
  // Audio
  webm: "audio/webm",
  mp3: "audio/mpeg",
  mp4: "audio/mp4",
  m4a: "audio/x-m4a",
  ogg: "audio/ogg",
  wav: "audio/wav",
  aac: "audio/aac",
  flac: "audio/flac",
};

/**
 * Get effective MIME type for a file.
 * Falls back to extension-based detection if browser MIME type is empty or generic.
 */
const getEffectiveMimeType = (file: File): string => {
  // If browser provides a valid MIME type, use it
  if (file.type && file.type !== "application/octet-stream") {
    return file.type;
  }

  // Fallback: detect from extension
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && EXTENSION_TO_MIME[ext]) {
    console.log(`📁 [useFileUpload] MIME fallback: ${file.name} → ${EXTENSION_TO_MIME[ext]} (from .${ext})`);
    return EXTENSION_TO_MIME[ext];
  }

  return file.type || "";
};

interface UseFileUploadOptions {
  initialBlocks?: ContentBlock[];
  enabled?: boolean;
  /** Callback to handle audio files separately (they go through a different upload flow) */
  onAudioFile?: (file: File) => Promise<void>;
}

// Helper to generate unique ID for tracking during processing
// Uses UUID so it can be directly used as Firestore message ID
const generateBlockId = () => uuidv4();

// Helper to create placeholder blocks with File objects for instant preview
const createPlaceholders = (files: File[]): Base64ContentBlock[] => {
  return files.map(file => ({
    type: isSupportedImageType(file.type) ? ("image" as const) : ("file" as const),
    source_type: "base64" as const,
    mime_type: file.type,
    data: "",  // Empty during processing
    file,  // Original file for object URL
    processing: true,
    processingStage: "optimizing" as const,
    id: generateBlockId(),  // Pre-generated message ID for Firestore
    metadata: { name: file.name, size: file.size }
  }));
};

/**
 * Validate file size and dimensions
 * Returns null if valid, otherwise returns error message
 */
const validateFileWithDimensions = async (
  file: File,
): Promise<string | null> => {
  // First check file type and size
  const sizeValidation = validateFile(file);
  if (!sizeValidation.valid) {
    return sizeValidation.error || "File validation failed";
  }

  // For images, also check dimensions
  if (isSupportedImageType(file.type)) {
    const dimensionValidation = await validateImageDimensions(file);
    if (!dimensionValidation.valid) {
      return dimensionValidation.error || "Image dimension validation failed";
    }
  }

  return null;
};

export function useFileUpload({
  initialBlocks = [],
  enabled = true,
  onAudioFile,
}: UseFileUploadOptions = {}) {
  const [contentBlocks, setContentBlocks] =
    useState<ContentBlock[]>(initialBlocks);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);

  const isDuplicate = (file: File, blocks: ContentBlock[]) => {
    if (file.type === "application/pdf") {
      return blocks.some(
        (b) =>
          b.type === "file" &&
          b.source_type === "base64" &&
          b.mime_type === "application/pdf" &&
          b.metadata?.filename === file.name,
      );
    }
    if (isSupportedFileType(file.type)) {
      return blocks.some(
        (b) =>
          b.type === "image" &&
          b.source_type === "base64" &&
          b.metadata?.name === file.name &&
          b.mime_type === file.type,
      );
    }
    return false;
  };

  /**
   * Optimize image for Anthropic API (resize + compress)
   * Also extracts photo date from EXIF before compression (which strips EXIF)
   * Returns optimized file with extracted metadata options
   */
  const processFileWithOptimization = useCallback(async (file: File): Promise<{
    file: File;
    options: FileToContentBlockOptions;
  }> => {
    const options: FileToContentBlockOptions = {};

    // Extract photo date from EXIF BEFORE compression (compression strips EXIF)
    if (isSupportedImageType(file.type)) {
      const photoDate = await extractPhotoDate(file);
      if (photoDate) {
        options.photoDate = photoDate.toISOString();
      }
    }

    // Only optimize images that support compression
    if (!isSupportedImageType(file.type) || !isCompressibleImage(file.type)) {
      return { file, options };
    }

    try {
      const result = await optimizeForAnthropicApi(file);
      return { file: result.file, options };
    } catch (error) {
      // Return original file if optimization fails
      console.warn("Image optimization failed:", error);
      return { file, options };
    }
  }, []);

  /**
   * Count total images in blocks
   */
  const countImages = (blocks: ContentBlock[]): number => {
    return blocks.filter((b) => b.type === "image").length;
  };

  /**
   * Process files in background and update blocks progressively
   * Each file goes through: optimize → validate → encode → thumbnail
   * Blocks are updated with processing stage and replaced with final version when done
   */
  const processFilesInBackground = useCallback(async (
    files: File[],
    placeholderIds: string[]
  ) => {
    // Process all files in parallel for better UX
    await Promise.allSettled(files.map(async (file, i) => {
      const tempId = placeholderIds[i];

      try {
        // Stage 1: Extract EXIF + optimize
        const processed = await processFileWithOptimization(file);

        // Stage 2: Validate
        setContentBlocks(prev => prev.map(b => {
          if ('id' in b && b.id === tempId) {
            return { ...b, processingStage: "validating" as const };
          }
          return b;
        }));

        const error = await validateFileWithDimensions(processed.file);
        if (error) {
          toast.error(error);
          // Remove block on error
          setContentBlocks(prev => prev.filter(b =>
            !('id' in b) || b.id !== tempId
          ));
          return;
        }

        // Check limits BEFORE encoding (avoid wasted work)
        const currentImageCount = countImages(contentBlocks);
        if (
          isSupportedImageType(processed.file.type) &&
          currentImageCount >= FILE_SIZE_LIMITS.MAX_IMAGES_PER_REQUEST
        ) {
          toast.error(
            getFileUploadError(FileUploadError.TOO_MANY_IMAGES, {
              count: String(currentImageCount + 1),
            })
          );
          setContentBlocks(prev => prev.filter(b =>
            !('id' in b) || b.id !== tempId
          ));
          return;
        }

        // Stage 3: Encode to base64 + generate thumbnail
        setContentBlocks(prev => prev.map(b => {
          if ('id' in b && b.id === tempId) {
            return { ...b, processingStage: "encoding" as const };
          }
          return b;
        }));

        const finalBlock = await fileToContentBlock(processed.file, processed.options);

        // Replace placeholder with final block (remove processing fields but KEEP id)
        // id is preserved for deduplication in useAutoUpload AND used as Firestore message ID
        setContentBlocks(prev => prev.map(b => {
          if ('id' in b && b.id === tempId) {
            return {
              ...finalBlock,
              file: undefined,
              processing: undefined,
              processingStage: undefined,
              id: tempId,  // Preserve for deduplication AND Firestore message ID
            } as ContentBlock;
          }
          return b;
        }));

      } catch (error) {
        console.error("File processing failed:", error);
        toast.error(`Bestand "${file.name}" verwerken mislukt`);
        // Remove block on error
        setContentBlocks(prev => prev.filter(b =>
          !('id' in b) || b.id !== tempId
        ));
      }
    }));
  }, [processFileWithOptimization, contentBlocks]);

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const fileArray = Array.from(files);

    // Debug: log all file types (with effective MIME type fallback)
    console.log("📁 [useFileUpload] handleFileUpload - all files:", fileArray.map(f => ({
      name: f.name,
      browserType: f.type,
      effectiveType: getEffectiveMimeType(f),
    })));

    // Use effective MIME type (with extension fallback) for validation
    const validFiles = fileArray.filter((file) =>
      isSupportedFileType(getEffectiveMimeType(file)),
    );
    const invalidFiles = fileArray.filter(
      (file) => !isSupportedFileType(getEffectiveMimeType(file)),
    );
    const duplicateFiles = validFiles.filter((file) =>
      isDuplicate(file, contentBlocks),
    );
    const uniqueFiles = validFiles.filter(
      (file) => !isDuplicate(file, contentBlocks),
    );

    console.log("📁 [useFileUpload] handleFileUpload - categorized:", {
      valid: validFiles.length,
      invalid: invalidFiles.map(f => ({ name: f.name, type: f.type, effective: getEffectiveMimeType(f) })),
      hasOnAudioFile: !!onAudioFile,
    });

    if (invalidFiles.length > 0) {
      toast.error(getFileUploadError(FileUploadError.INVALID_TYPE_UPLOAD));
    }
    if (duplicateFiles.length > 0) {
      toast.error(
        getFileUploadError(FileUploadError.DUPLICATE_FILES, {
          files: duplicateFiles.map((f) => f.name).join(", "),
        }),
      );
    }

    // Separate audio files from other files (audio has different upload flow)
    // Use effective MIME type for proper detection
    const audioFiles = uniqueFiles.filter((file) => isSupportedAudioType(getEffectiveMimeType(file)));
    const nonAudioFiles = uniqueFiles.filter((file) => !isSupportedAudioType(getEffectiveMimeType(file)));

    console.log("📁 [useFileUpload] Audio routing:", {
      audioFiles: audioFiles.map(f => ({ name: f.name, type: f.type })),
      nonAudioFiles: nonAudioFiles.length,
    });

    // Route audio files to dedicated handler
    if (onAudioFile && audioFiles.length > 0) {
      for (const audioFile of audioFiles) {
        console.log("📤 [useFileUpload] Routing audio to handler:", audioFile.name);
        onAudioFile(audioFile);
      }
    }

    // Process non-audio files as before
    if (nonAudioFiles.length > 0) {
      const placeholders = createPlaceholders(nonAudioFiles);
      const placeholderIds = placeholders.map(p => p.id!);
      setContentBlocks((prev) => [...prev, ...placeholders]);

      // Process files in background
      processFilesInBackground(nonAudioFiles, placeholderIds);
    }

    // Reset input
    e.target.value = "";
  };

  // Drag and drop handlers
  // Note: We use window-level handlers that don't depend on dropRef,
  // but dropRef is used for element-specific visual feedback if available
  useEffect(() => {
    if (!enabled) return;

    // Global drag events with counter for robust dragOver state
    const handleWindowDragEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        dragCounter.current += 1;
        setDragOver(true);
      }
    };
    const handleWindowDragLeave = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) {
          setDragOver(false);
          dragCounter.current = 0;
        }
      }
    };
    const handleWindowDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragOver(false);

      if (!e.dataTransfer) return;

      const files = Array.from(e.dataTransfer.files);

      // Debug: log all dropped files with effective MIME types
      console.log("📁 [useFileUpload] handleWindowDrop - all files:", files.map(f => ({
        name: f.name,
        browserType: f.type,
        effectiveType: getEffectiveMimeType(f),
      })));

      // Use effective MIME type (with extension fallback) for validation
      const validFiles = files.filter((file) =>
        isSupportedFileType(getEffectiveMimeType(file)),
      );
      const invalidFiles = files.filter(
        (file) => !isSupportedFileType(getEffectiveMimeType(file)),
      );
      const duplicateFiles = validFiles.filter((file) =>
        isDuplicate(file, contentBlocks),
      );
      const uniqueFiles = validFiles.filter(
        (file) => !isDuplicate(file, contentBlocks),
      );

      if (invalidFiles.length > 0) {
        toast.error(getFileUploadError(FileUploadError.INVALID_TYPE_UPLOAD));
      }
      if (duplicateFiles.length > 0) {
        toast.error(
          getFileUploadError(FileUploadError.DUPLICATE_FILES, {
            files: duplicateFiles.map((f) => f.name).join(", "),
          }),
        );
      }

      // Separate audio files from other files (audio has different upload flow)
      // Use effective MIME type for proper detection
      const audioFiles = uniqueFiles.filter((file) => isSupportedAudioType(getEffectiveMimeType(file)));
      const nonAudioFiles = uniqueFiles.filter((file) => !isSupportedAudioType(getEffectiveMimeType(file)));

      // Route audio files to dedicated handler
      if (onAudioFile && audioFiles.length > 0) {
        for (const audioFile of audioFiles) {
          onAudioFile(audioFile);
        }
      }

      // Process non-audio files as before
      if (nonAudioFiles.length > 0) {
        const placeholders = createPlaceholders(nonAudioFiles);
        const placeholderIds = placeholders.map(p => p.id!);
        setContentBlocks((prev) => [...prev, ...placeholders]);

        // Process files in background
        processFilesInBackground(nonAudioFiles, placeholderIds);
      }
    };
    const handleWindowDragEnd = (e: DragEvent) => {
      dragCounter.current = 0;
      setDragOver(false);
    };
    window.addEventListener("dragenter", handleWindowDragEnter);
    window.addEventListener("dragleave", handleWindowDragLeave);
    window.addEventListener("drop", handleWindowDrop);
    window.addEventListener("dragend", handleWindowDragEnd);

    // Prevent default browser behavior for dragover globally
    const handleWindowDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("dragover", handleWindowDragOver);

    // Element-specific handlers for visual feedback (optional, only if dropRef is attached)
    const element = dropRef.current;
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    };
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    };
    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
    };

    if (element) {
      element.addEventListener("dragover", handleDragOver);
      element.addEventListener("dragenter", handleDragEnter);
      element.addEventListener("dragleave", handleDragLeave);
    }

    return () => {
      if (element) {
        element.removeEventListener("dragover", handleDragOver);
        element.removeEventListener("dragenter", handleDragEnter);
        element.removeEventListener("dragleave", handleDragLeave);
      }
      window.removeEventListener("dragenter", handleWindowDragEnter);
      window.removeEventListener("dragleave", handleWindowDragLeave);
      window.removeEventListener("drop", handleWindowDrop);
      window.removeEventListener("dragend", handleWindowDragEnd);
      window.removeEventListener("dragover", handleWindowDragOver);
      dragCounter.current = 0;
    };
  }, [contentBlocks, enabled, processFileWithOptimization, processFilesInBackground, onAudioFile]);

  const removeBlock = (idx: number) => {
    setContentBlocks((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetBlocks = () => setContentBlocks([]);

  /**
   * Handle paste event for files (images, PDFs)
   * Can be used as onPaste={handlePaste} on a textarea or input
   */
  const handlePaste = async (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const items = e.clipboardData.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length === 0) {
      return;
    }
    e.preventDefault();

    // Use effective MIME type (with extension fallback) for validation
    const validFiles = files.filter((file) =>
      isSupportedFileType(getEffectiveMimeType(file)),
    );
    const invalidFiles = files.filter(
      (file) => !isSupportedFileType(getEffectiveMimeType(file)),
    );
    const isDuplicatePaste = (file: File) => {
      const effectiveType = getEffectiveMimeType(file);
      if (effectiveType === SUPPORTED_PDF_TYPE) {
        return contentBlocks.some(
          (b) =>
            b.type === "file" &&
            b.mime_type === "application/pdf" &&
            b.metadata?.filename === file.name,
        );
      }
      if (isSupportedFileType(effectiveType)) {
        return contentBlocks.some(
          (b) =>
            b.type === "image" &&
            b.metadata?.name === file.name &&
            b.mime_type === effectiveType,
        );
      }
      return false;
    };
    const duplicateFiles = validFiles.filter(isDuplicatePaste);
    const uniqueFiles = validFiles.filter((file) => !isDuplicatePaste(file));
    if (invalidFiles.length > 0) {
      toast.error(getFileUploadError(FileUploadError.INVALID_TYPE_PASTE));
    }
    if (duplicateFiles.length > 0) {
      toast.error(
        getFileUploadError(FileUploadError.DUPLICATE_FILES, {
          files: duplicateFiles.map((f) => f.name).join(", "),
        }),
      );
    }

    // Separate audio files from other files (audio has different upload flow)
    // Use effective MIME type for proper detection
    const audioFiles = uniqueFiles.filter((file) => isSupportedAudioType(getEffectiveMimeType(file)));
    const nonAudioFiles = uniqueFiles.filter((file) => !isSupportedAudioType(getEffectiveMimeType(file)));

    // Route audio files to dedicated handler
    if (onAudioFile && audioFiles.length > 0) {
      for (const audioFile of audioFiles) {
        onAudioFile(audioFile);
      }
    }

    // Process non-audio files as before
    if (nonAudioFiles.length > 0) {
      const placeholders = createPlaceholders(nonAudioFiles);
      const placeholderIds = placeholders.map(p => p.id!);
      setContentBlocks((prev) => [...prev, ...placeholders]);

      // Process files in background
      processFilesInBackground(nonAudioFiles, placeholderIds);
    }
  };

  /**
   * Update the description and optional audio URLs for an image block at the given index
   */
  const updateBlockDescription = (idx: number, description: string, descriptionAudioUrls?: string[]) => {
    setContentBlocks((prev) => {
      const updated = prev.map((block, i): ContentBlock => {
        if (i !== idx) return block;
        // Only update image blocks
        if (block.type !== "image") {
          return block;
        }

        // Handle storage image blocks
        if ("source_type" in block && block.source_type === "storage") {
          const newBlock = {
            ...block,
            metadata: {
              ...block.metadata,
              description,
              descriptionAudioUrls,
            },
          } as ContentBlock;
          return newBlock;
        }

        // Handle base64 image blocks
        const newBlock = {
          ...block,
          metadata: {
            ...block.metadata,
            description,
            descriptionAudioUrls,
          },
        } as ContentBlock;
        return newBlock;
      });
      return updated;
    });
  };

  return {
    contentBlocks,
    setContentBlocks,
    handleFileUpload,
    dropRef,
    removeBlock,
    resetBlocks,
    dragOver,
    handlePaste,
    updateBlockDescription,
  };
}
